import { fabric } from 'fabric';
import { setImageOnSelect } from '../../features/card-editor/utils/add-photo-utils';
import { OnPhotoTrayImageDrop } from '../../global-types/canvas';
import { linkPhoto } from '../../services';
import { defaultImageSettingsConfig, scaleToHeight, scaleToWidth } from '../configs/default-image-settings.config';
import { CanvasDataTypes } from './canvas-data-types';
import { loadImageWithRetry } from './load-image-with-retry';
import { removeExistingPhotoZoneImage } from './remove-existing-photo-zone-image';
import { resolvePhotoZoneIdFromEvent } from './resolve-photo-zone-id-from-event';

export const onPhotoTrayImageDrop = async ({
  event,
  data,
  isUK,
  setIsImageUploadDrawerOpen,
  hideMiddleControls,
  canvas,
  appDispatch,
  openImageEditDrawer,
  isReplacingImage,
  updateEditFormats,
  firstElementSelected,
  fillPhotoZone,
  handleIconAdded,
  addAction,
  onZoneFilled,
  isSignAndSend,
  currentCardFace,
  addPhotoToCardFace,
  runHistoryTransaction,
}: OnPhotoTrayImageDrop) => {
  event.e.preventDefault();
  event.e.stopPropagation();
  const dragEvent = event.e as DragEvent;
  const id = dragEvent.dataTransfer?.getData('id');
  if (!id) return;

  // PRCM-11096: Sign & Send cards expose no photo option anywhere, so there is
  // nothing to place — ignore the drop entirely.
  if (isSignAndSend) {
    return;
  }

  // PRCM-10916: resolve which photo zone (if any) the drop landed on BEFORE
  // doing the (expensive) image load/link work, so a drop that should be
  // ignored can bail out without side effects.
  const photozoneId = resolvePhotoZoneIdFromEvent(canvas, event);

  // PRCM-11096: a face with zones but a drop that missed them all is ignored
  // (no free-placement fallback on a zoned face). Only a face with ZERO zones
  // gets free-placement.
  const hasZones = (currentCardFace?.zones?.length ?? 0) > 0;
  if (!photozoneId && hasZones) {
    return;
  }
  // PRCM-11096: without a resolved zone and without a way to free-place, bail.
  if (!photozoneId && !addPhotoToCardFace) {
    return;
  }

  const el = document.getElementById(id) as HTMLImageElement;
  const imgEl = await loadImageWithRetry(el.src);

  const payload = {
    image_reference_id: id,
  };

  const response = await linkPhoto(payload, data?.project_id as string);

  // PRCM-11096: free-placement branch — no zone under the drop, and the face
  // has no zones. Build a plain user image (parity with the click free-add
  // flow) positioned at the drop point, then route through `addPhotoToCardFace`
  // so it shares the exact same initialization/lifecycle as a click-placed
  // image (edit drawer, analytics, selection, boundary constraint).
  if (!photozoneId) {
    const freeImage = new fabric.Image(imgEl, {
      name: response.data?.image_id,
      data: {
        image_reference_id: id,
        type: CanvasDataTypes.UserImage,
      },
      borderColor: isUK ? '#333333' : '#604099',
    });

    if (!freeImage.width || !freeImage.height) {
      throw new Error('Image has zero dimensions after loading.');
    }

    // Pre-scale to the same size `addImageToCanvas` uses so we can center the
    // image on the drop point (addPhotoToCardFace re-scales identically and
    // preserves this left/top since real cards have no clip-path override).
    freeImage.scaleToHeight(scaleToHeight);
    freeImage.scaleToWidth(scaleToWidth);
    const pointer = canvas.getPointer(event.e);
    freeImage.set({
      left: pointer.x - freeImage.getScaledWidth() / 2,
      top: pointer.y - freeImage.getScaledHeight() / 2,
    });

    await addPhotoToCardFace?.(freeImage, { preservePosition: true });
    return;
  }

  const settings = {
    ...defaultImageSettingsConfig({ event, data: response.data, isUK }),
  };

  const imgInstance = new fabric.Image(imgEl, settings);

  if (!imgInstance.width || !imgInstance.height) {
    throw new Error('Image has zero dimensions after loading.');
  }

  imgInstance.originX = 'left';
  imgInstance.originY = 'top';
  imgInstance.scaleToHeight(scaleToHeight);
  imgInstance.scaleToWidth(scaleToWidth);

  setIsImageUploadDrawerOpen(appDispatch);
  hideMiddleControls(imgInstance);

  // PRCM-10916: mirror the click-to-fill flow (`addPhotoToPhotozone`) exactly
  // — wire the select handler, fill the zone (clip + cover-scale), log the
  // same datadog action, then open the image edit drawer on the next tick.
  setImageOnSelect(imgInstance, openImageEditDrawer, isReplacingImage, updateEditFormats, firstElementSelected);
  // PRCM-10916: dropping on an occupied zone replaces the photo in place, so
  // clear any existing image + PhotoIcon first (same as the click flow) — the
  // drag-over highlight already opts into occupied zones (`allowOccupied`).
  await runHistoryTransaction(canvas, () => {
    removeExistingPhotoZoneImage(canvas, photozoneId);
    fillPhotoZone({ image: imgInstance, photozoneId, onIconAdded: handleIconAdded });
  });

  // PRCM-10916: mark the just-filled zone as the selected photo zone so a
  // subsequent tray-photo click replaces THIS image (parity with the click
  // flow) instead of falling back to the first empty zone. The zone now has
  // content, so the selection-highlight effect won't paint an overlay on it.
  onZoneFilled?.(photozoneId, imgInstance);

  addAction('add-photo-to-user-zone', {
    zoneId: photozoneId,
  });

  setTimeout(() => openImageEditDrawer(), 0);
};
